import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown, Zap, Crown, Sparkles, ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TranslatedText } from '@/components/TranslatedText';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { trackLead, trackInitiateCheckout, setAdvancedMatching, trackGoogleAdsSignup, trackGoogleAdsBeginCheckout, trackTaboolaLead, trackTaboolaStartCheckout } from '@/lib/metaPixel';
import { lovable } from '@/integrations/lovable/index';

const favoriteCountries = [
  "United States",
  "Canada", 
  "Australia",
  "United Kingdom",
];

const allCountries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cambodia", "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman",
  "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar",
  "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "São Tomé and Príncipe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen",
  "Zambia", "Zimbabwe",
  "Other"
];

const otherCountries = allCountries.filter(c => !favoriteCountries.includes(c));

const applicationSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name must be 100 characters or less"),
  email: z.string().trim().email("Invalid email address").max(255),
  country: z.string().min(1, "Please select your country"),
  plan: z.enum(['monthly', 'lifetime', 'free_trial'], { required_error: "Please select a plan" }),
});

type ApplicationData = z.infer<typeof applicationSchema>;

const Apply = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { monthlyPrice, lifetimePrice, stripeMonthlyLink, stripeLifetimeLink } = useAdminSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ApplicationData>({
    name: '',
    email: '',
    country: '',
    plan: 'lifetime',
  });
  const [countryOpen, setCountryOpen] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ApplicationData, string>>>({});
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [showCountryConfirm, setShowCountryConfirm] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // Capture referral code from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  // Auto-detect country via IP geolocation
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        const countryName = data.country_name;
        if (countryName && allCountries.includes(countryName)) {
          setDetectedCountry(countryName);
          setFormData(prev => prev.country ? prev : { ...prev, country: countryName });
        }
      } catch (e) {
        console.warn('Country detection failed:', e);
      }
    };
    detectCountry();
  }, []);

  const getStripeUrlWithEmail = (baseUrl: string, email: string) => {
    const successUrl = `${window.location.origin}/payment-success`;
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}prefilled_email=${encodeURIComponent(email)}&success_url=${encodeURIComponent(successUrl)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = applicationSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ApplicationData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof ApplicationData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    // Check if selected country differs from IP-detected country
    if (detectedCountry && formData.country !== detectedCountry) {
      setShowCountryConfirm(true);
      return;
    }

    await proceedWithSubmit();
  };

  const proceedWithSubmit = async () => {
    setIsSubmitting(true);
    setShowCountryConfirm(false);

    try {
      const today = new Date();
      const endDate = new Date(today);
      const isFreeTrial = formData.plan === 'free_trial';
      
      if (formData.plan === 'monthly') {
        endDate.setMonth(endDate.getMonth() + 1);
      } else if (isFreeTrial) {
        endDate.setDate(endDate.getDate() + 7);
      } else {
        endDate.setFullYear(endDate.getFullYear() + 100);
      }

      const planMap: Record<string, string> = {
        'lifetime': 'Lifetime',
        'monthly': 'Monthly',
        'free_trial': 'Freemium',
      };

      // Get referrer ID from referral code if provided
      let referrerId: string | null = null;
      if (referralCode) {
        const { data: referrerData } = await supabase.rpc('get_referrer_from_code', {
          p_referral_code: referralCode
        });
        if (referrerData) {
          referrerId = referrerData;
        }
      }

      const { error } = await supabase.from('clients').insert({
        name: formData.name,
        email: formData.email.toLowerCase().trim(),
        country: formData.country,
        detected_country: detectedCountry,
        applied_at: new Date().toISOString(),
        plan: planMap[formData.plan] || 'Monthly',
        plan_price: 0,
        status: isFreeTrial ? 'active' : 'inactive',
        paid: false,
        has_access: isFreeTrial,
        user_id: null,
        subscription_start: today.toISOString().split('T')[0],
        subscription_end: endDate.toISOString().split('T')[0],
        referral_code_used: referralCode,
        referred_by: referrerId,
      } as any);

      if (error) {
        console.error('Error creating client:', error);
        toast.error('Failed to create account. Please try again.');
        setIsSubmitting(false);
        return;
      }

      sessionStorage.setItem('applicationData', JSON.stringify({
        name: formData.name,
        email: formData.email,
        plan: formData.plan,
      }));

      if (isFreeTrial) {
        sessionStorage.setItem(
          'freemiumIntent',
          JSON.stringify({
            email: formData.email.toLowerCase().trim(),
            name: formData.name,
          })
        );
        // Fire Meta Pixel Lead event with Advanced Matching (email + country)
        setAdvancedMatching({ email: formData.email, country: formData.country });
        trackLead('Free Trial Signup', 'Freemium');
        trackGoogleAdsSignup('freemium');
        trackTaboolaLead();
        navigate('/set-password');
      } else {
        // Fire Pixel with Advanced Matching for paid plans too
        setAdvancedMatching({ email: formData.email, country: formData.country });
        trackInitiateCheckout(
          formData.plan === 'lifetime' ? 'Lifetime Access' : 'Monthly Access',
          formData.plan === 'lifetime' ? parseFloat(lifetimePrice) : parseFloat(monthlyPrice),
        );
        trackGoogleAdsBeginCheckout(
          formData.plan === 'lifetime' ? parseFloat(lifetimePrice) : parseFloat(monthlyPrice),
        );
        trackTaboolaStartCheckout();
        const link = formData.plan === 'lifetime' ? stripeLifetimeLink : stripeMonthlyLink;
        window.location.href = getStripeUrlWithEmail(link, formData.email);
      }
    } catch (err) {
      console.error('Error creating account:', err);
      toast.error('Failed to create account. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-card relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-xl"
        >
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              <TranslatedText>Create Account</TranslatedText>
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              <TranslatedText>Welcome! Start your journey with FA Club today.</TranslatedText>
            </p>
          </div>

          {/* Referral Code Banner */}
          {referralCode && (
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl text-center">
              <p className="text-sm text-primary">
               🎉 You were referred! Code: <span className="font-mono font-semibold">{referralCode}</span>
              </p>
            </div>
          )}

          {/* Form Card */}
          <div className="bg-card rounded-2xl shadow-xl border border-border p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground text-sm font-medium">
                  Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-muted border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-12 rounded-xl"
                  placeholder="Your name and surname"
                  disabled={isSubmitting}
                />
                {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-muted border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-12 rounded-xl"
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                />
                {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" className="text-foreground text-sm font-medium">
                  Country
                </Label>
                <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={countryOpen}
                      className="w-full justify-between bg-muted border-border hover:bg-muted/80 hover:border-primary/30 text-foreground font-normal h-12 rounded-xl"
                      disabled={isSubmitting}
                    >
                      {formData.country || "Select your country"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-card border-border z-50 max-h-[60vh]">
                    <Command className="bg-transparent">
                      <CommandInput placeholder="Search country..." className="border-border text-foreground" />
                      <CommandList className="max-h-[200px]">
                        <CommandEmpty className="text-muted-foreground py-4 text-center text-sm">No country found.</CommandEmpty>
                        <CommandGroup heading="Popular">
                          {favoriteCountries.map((country) => (
                            <CommandItem
                              key={country}
                              value={country}
                              onSelect={(currentValue) => {
                                setFormData({ ...formData, country: currentValue === formData.country ? "" : country });
                                setCountryOpen(false);
                              }}
                              className="text-foreground hover:bg-primary/5 cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.country === country ? "opacity-100 text-primary" : "opacity-0"
                                )}
                              />
                              {country}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        <CommandGroup heading="All Countries">
                          {otherCountries.map((country) => (
                            <CommandItem
                              key={country}
                              value={country}
                              onSelect={(currentValue) => {
                                setFormData({ ...formData, country: currentValue === formData.country ? "" : country });
                                setCountryOpen(false);
                              }}
                              className="text-foreground hover:bg-primary/5 cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.country === country ? "opacity-100 text-primary" : "opacity-0"
                                )}
                              />
                              {country}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.country && <p className="text-red-500 text-xs">{errors.country}</p>}
              </div>

              {/* Plan Selection */}
              <div className="space-y-3">
                <Label className="text-foreground text-sm font-medium">
                  Select Your Plan
                </Label>
                <div className="grid grid-cols-1 gap-3">
                  {/* Lifetime Plan */}
                  <motion.button
                    type="button"
                    onClick={() => setFormData({ ...formData, plan: 'lifetime' })}
                    disabled={isSubmitting}
                    className={cn(
                      "relative p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-300 active:scale-[0.98]",
                      formData.plan === 'lifetime'
                        ? "border-primary bg-primary/5 shadow-lg shadow-primary/20"
                        : "border-border bg-muted hover:border-primary/40"
                    )}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="absolute -top-2 right-3 sm:right-4">
                      <span className="px-2 py-0.5 text-[10px] font-medium tracking-wider uppercase bg-primary text-primary-foreground rounded-full">
                        Best Value
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                          formData.plan === 'lifetime' ? "bg-primary/10" : "bg-muted"
                        )}>
                          <Crown className={cn(
                            "w-5 h-5",
                            formData.plan === 'lifetime' ? "text-primary" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-foreground text-sm sm:text-base">Lifetime Access</h3>
                          <p className="text-xs text-muted-foreground">One-time payment, forever</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl sm:text-2xl font-bold text-primary">${lifetimePrice}</p>
                        <p className="text-xs text-muted-foreground">one-time</p>
                      </div>
                    </div>
                  </motion.button>

                  {/* Monthly Plan */}
                  <motion.button
                    type="button"
                    onClick={() => setFormData({ ...formData, plan: 'monthly' })}
                    disabled={isSubmitting}
                    className={cn(
                      "relative p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-300 active:scale-[0.98]",
                      formData.plan === 'monthly'
                        ? "border-primary bg-primary/5 shadow-lg shadow-primary/20"
                        : "border-border bg-muted hover:border-primary/40"
                    )}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                          formData.plan === 'monthly' ? "bg-primary/10" : "bg-muted"
                        )}>
                          <Zap className={cn(
                            "w-5 h-5",
                            formData.plan === 'monthly' ? "text-primary" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-foreground text-sm sm:text-base">Monthly Access</h3>
                          <p className="text-xs text-muted-foreground">Billed monthly, cancel anytime</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl sm:text-2xl font-bold text-primary">${monthlyPrice}</p>
                        <p className="text-xs text-muted-foreground">/month</p>
                      </div>
                    </div>
                  </motion.button>

                  {/* Free Trial */}
                  <motion.button
                    type="button"
                    onClick={() => setFormData({ ...formData, plan: 'free_trial' })}
                    disabled={isSubmitting}
                    className={cn(
                      "relative p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-300 active:scale-[0.98]",
                      formData.plan === 'free_trial'
                        ? "border-green-500 bg-green-50 shadow-lg shadow-green-500/20"
                        : "border-border bg-muted hover:border-green-300"
                    )}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                          formData.plan === 'free_trial' ? "bg-green-100" : "bg-muted"
                        )}>
                          <Sparkles className={cn(
                            "w-5 h-5",
                            formData.plan === 'free_trial' ? "text-green-600" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-foreground text-sm sm:text-base">Watch 1 FREE Class</h3>
                          <p className="text-xs text-green-600 font-medium">No credit card needed</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl sm:text-2xl font-bold text-green-600">FREE</p>
                        <p className="text-xs text-muted-foreground">Limited access</p>
                      </div>
                    </div>
                  </motion.button>
                </div>
                {errors.plan && <p className="text-red-500 text-xs">{errors.plan}</p>}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full h-14 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shadow-lg shadow-primary/25 active:scale-[0.98]"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : formData.plan === 'free_trial' ? (
                  'Get Free Access'
                ) : (
                  'Explore Our Academy →'
                )}
              </Button>

              {/* Divider */}
              <div className="my-4 flex items-center gap-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-muted-foreground text-sm">or sign up with</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Social Signup Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={async () => {
                    const { error } = await lovable.auth.signInWithOAuth('google', {
                      redirect_uri: window.location.origin,
                    });
                    if (error) toast.error(error.message || 'Failed to sign in with Google');
                  }}
                  className="h-12 rounded-xl border-border hover:border-primary/30 text-foreground font-medium"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={async () => {
                    const { error } = await lovable.auth.signInWithOAuth('apple', {
                      redirect_uri: window.location.origin,
                    });
                    if (error) toast.error(error.message || 'Failed to sign in with Apple');
                  }}
                  className="h-12 rounded-xl border-border hover:border-primary/30 text-foreground font-medium"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-1.55 4.14-3.74 4.25z"/>
                  </svg>
                  Apple
                </Button>
              </div>

              <div className="space-y-2 pt-2">
                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </form>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <TranslatedText>Back to Home</TranslatedText>
            </Link>
          </div>

          {/* Trust indicator */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-4 h-4 text-green-500" />
            <span><TranslatedText>Secure checkout • 7-day money-back guarantee</TranslatedText></span>
          </div>
        </motion.div>
      </div>

      {/* Country mismatch confirmation dialog */}
      <Dialog open={showCountryConfirm} onOpenChange={(open) => {
        setShowCountryConfirm(open);
        if (!open) setIsSubmitting(false);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Your Country
            </DialogTitle>
            <DialogDescription>
              We detected your location as <strong>{detectedCountry}</strong>, but you selected <strong>{formData.country}</strong>. Is <strong>{formData.country}</strong> correct?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setFormData(prev => ({ ...prev, country: detectedCountry || '' }));
                setShowCountryConfirm(false);
              }}
            >
              Use {detectedCountry}
            </Button>
            <Button onClick={() => proceedWithSubmit()}>
              Yes, {formData.country} is correct
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Apply;
